/* Notification settings — which channels reach you.
 *
 * "Notification service with per-user channel preferences", from the client's
 * own architecture notes.
 *
 * THE OPT-OUT EXISTS BECAUSE OF BULK MESSAGING. Every other notification in
 * the product happens because of something you did — somebody answered your
 * comment, prayed for your request. A church announcement arrives because of
 * who you ARE, so it is the one kind a person may reasonably not want on
 * their phone at seven in the morning.
 *
 * WHAT THIS DOES NOT DO: switch the bell off entirely. The in-app entry is
 * how the product keeps a record of what you were told — turning it off would
 * mean a member could be unreachable by their own church, which is not a
 * setting a church platform should offer.
 */
import { AlertCircle, Bell, Loader2, MessageSquare, Smartphone } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { useChannelPreferences, useSetChannelPreferences } from '@/api/queries'
import { cn } from '@/lib/utils'

export function NotificationSettingsPage() {
  const prefs = useChannelPreferences()
  const save = useSetChannelPreferences()

  if (prefs.isLoading) {
    return (
      <Card className="p-s6 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  if (prefs.isError) {
    return (
      <Card className="p-s6 text-center">
        <p className="font-semibold">These settings did not load.</p>
        <p className="mt-1 text-base text-ink-2">
          {(prefs.error as Error)?.message}
        </p>
      </Card>
    )
  }

  const current = prefs.data ?? { in_app: true, push: true, sms: true }

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-lg font-extrabold tracking-tight">Notifications</h1>
        <p className="text-base text-ink-2">
          Choose how your church reaches you.
        </p>
      </header>

      <Card className="divide-y divide-line-2">
        <ChannelRow
          icon={<Bell className="h-5 w-5" aria-hidden />}
          title="In the app"
          detail="Announcements appear in your notifications. This is how the app keeps a record of what you were told."
          checked
          /* Always on, and said plainly rather than shown as a switch that
             refuses to move. */
          locked
          lockReason="Always on"
          onChange={() => undefined}
        />

        <ChannelRow
          icon={<Smartphone className="h-5 w-5" aria-hidden />}
          title="Push notifications"
          detail="Your phone buzzes when your church posts something."
          checked={current.push}
          saving={save.isPending}
          onChange={(next) => save.mutate({ push: next })}
        />

        <ChannelRow
          icon={<MessageSquare className="h-5 w-5" aria-hidden />}
          title="SMS"
          detail="A text message, when your church sends one."
          checked={current.sms}
          saving={save.isPending}
          onChange={(next) => save.mutate({ sms: next })}
        />
      </Card>

      {save.isError ? (
        <p
          role="alert"
          className="flex items-start gap-s2 rounded-md bg-danger-soft px-s3 py-s2 text-base text-danger-ink"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          That did not save. {(save.error as Error)?.message}
        </p>
      ) : null}

      <p className="px-s1 text-sm text-ink-2">
        These apply to messages from your church. Replies to your own posts and
        prayers always reach you.
      </p>
    </div>
  )
}

function ChannelRow({
  icon,
  title,
  detail,
  checked,
  locked,
  lockReason,
  saving,
  onChange,
}: {
  icon: React.ReactNode
  title: string
  detail: string
  checked: boolean
  locked?: boolean
  lockReason?: string
  saving?: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-start gap-s3 p-s4">
      <span
        className={cn(
          'mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-pill',
          checked && !locked
            ? 'bg-brand-soft text-brand'
            : 'bg-surface-2 text-ink-2',
        )}
      >
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        <div className="font-semibold">{title}</div>
        <p className="mt-0.5 text-base text-ink-2">{detail}</p>
      </div>

      {locked ? (
        <span className="mt-s2 shrink-0 text-sm font-semibold text-ink-3">
          {lockReason}
        </span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={title}
          disabled={saving}
          onClick={() => onChange(!checked)}
          className={cn(
            'mt-s1 h-6 w-11 shrink-0 rounded-pill p-0.5 transition-colors',
            checked ? 'bg-brand' : 'bg-line',
            saving && 'opacity-60',
          )}
        >
          <span
            className={cn(
              'block h-5 w-5 rounded-pill bg-surface shadow-1 transition-transform',
              checked && 'translate-x-5',
            )}
          />
        </button>
      )}
    </div>
  )
}
